import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const SantaAnaVietnameseMarketRealEstatePage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?city=Santa+Ana&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching Santa Ana properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Santa Ana Vietnamese Market Real Estate | Homes for Sale | Truc Doan</title>
        <meta name="description" content="Find homes for sale near Santa Ana's Vietnamese market district. Affordable alternative to Westminster with growing Vietnamese businesses, 4th Street market area, and strong community. Contact Truc Doan." />
        <meta name="keywords" content="Santa Ana Vietnamese market real estate, homes near 4th Street Santa Ana, Vietnamese community Santa Ana, affordable homes Santa Ana CA, Santa Ana Vietnamese businesses, homes for sale Santa Ana Vietnamese area, Truc Doan Santa Ana, alternative to Westminster real estate" />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/santa-ana-vietnamese-market-real-estate`} />
        <meta property="og:title" content="Santa Ana Vietnamese Market Real Estate | Homes for Sale | Truc Doan" />
        <meta property="og:description" content="Discover affordable homes near Santa Ana's thriving Vietnamese market district. Growing businesses, strong community, and great value." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/santa-ana-vietnamese-market-real-estate`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Santa Ana, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": SEO_CONFIG.AGENT_PHONE,
            "email": SEO_CONFIG.AGENT_EMAIL,
            "url": `${SEO_CONFIG.DOMAIN}/santa-ana-vietnamese-market-real-estate`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Truc Doan helps buyers find homes near Santa Ana's Vietnamese market district. Affordable alternatives to Westminster with growing Vietnamese businesses and strong community.",
            "areaServed": {
              "@type": "City",
              "name": "Santa Ana, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button onClick={() => navigate('/')} style={{ position: 'absolute', top: '20px', left: '20px', padding: '10px 20px', background: 'rgb(14, 165, 233)', color: 'rgb(243, 244, 246)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', zIndex: 10 }}>
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>Santa Ana Vietnamese Market Real Estate</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            Affordable homes near Santa Ana's thriving Vietnamese market district — the smart alternative to Westminster.
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>Santa Ana's Growing Vietnamese Community</h2>
          <p>Santa Ana has emerged as an exciting and affordable alternative to Westminster for Vietnamese-American homebuyers. The city's Vietnamese community has grown substantially, anchored by the vibrant 4th Street market area where Vietnamese-owned restaurants, shops, and services create a welcoming cultural hub.</p>
          <p>With home prices significantly lower than neighboring Westminster and Garden Grove, Santa Ana offers Vietnamese families the opportunity to purchase larger homes, build equity faster, and enjoy the same cultural amenities — all while being just minutes from Little Saigon.</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>Why Vietnamese Buyers Choose Santa Ana</h2>
          <p>Santa Ana combines affordability with cultural richness, making it ideal for families and investors.</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>Affordable Alternative to Westminster</h3>
              <p>Santa Ana homes are priced 15-25% below comparable properties in Westminster, allowing buyers to get more space and value for their investment.</p>
            </div>
            <div className="amenity">
              <h3>4th Street Market Area</h3>
              <p>The 4th Street corridor features authentic Vietnamese restaurants, phở shops, bánh mì delis, and specialty markets — a growing cultural destination in its own right.</p>
            </div>
            <div className="amenity">
              <h3>Growing Vietnamese Businesses</h3>
              <p>Vietnamese-owned professional services, medical offices, legal practices, and retail stores are expanding throughout Santa Ana, building economic opportunity.</p>
            </div>
            <div className="amenity">
              <h3>Proximity to Little Saigon</h3>
              <p>Santa Ana borders Westminster directly — Little Saigon's shops, restaurants, and cultural events are just a 5-10 minute drive away.</p>
            </div>
            <div className="amenity">
              <h3>Investment Potential</h3>
              <p>With ongoing revitalization and growing demand from Vietnamese buyers, Santa Ana properties near the Vietnamese market area show strong appreciation potential.</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>Santa Ana Vietnamese Area Market Snapshot</h2>
          <p>The Santa Ana market offers excellent value compared to surrounding cities while still providing strong appreciation.</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Stat</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Median Home Price</td><td>~$750,000</td></tr>
              <tr><td>Average Days on Market</td><td>22–32 Days</td></tr>
              <tr><td>Most Popular Price Range</td><td>$550K – $900K</td></tr>
              <tr><td>Price Advantage vs Westminster</td><td>15–25% lower</td></tr>
              <tr><td>Distance to Little Saigon</td><td>5–10 minutes</td></tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>Santa Ana Homes Currently for Sale</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>{t('property.loadingProperties')}</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div key={property.listing_key} className="property-card" onClick={() => navigate(`/listing/${property.listing_key}`)} style={{ backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center' }}>
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">{formatPropertyAddress(property)}, CA {property.postal_code}</div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>{t('landing.listedBy')}: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>Contact Truc Doan for the latest Santa Ana listings near the Vietnamese market area.</p>
          )}
        </section>

        <section className="cta-section">
          <h2>Find Your Home Near Santa Ana's Vietnamese Market</h2>
          <p>Truc Doan knows the Santa Ana Vietnamese community and can help you find the perfect home at an affordable price. Bilingual service in Vietnamese and English.</p>
          <div className="cta-buttons">
            <a href={`mailto:${SEO_CONFIG.AGENT_EMAIL}?subject=Santa Ana Vietnamese Market Area Home Inquiry`} className="cta-button primary">{t('county.emailAgent')}</a>
            <a href={`tel:${SEO_CONFIG.AGENT_PHONE}`} className="cta-button secondary">{t('county.callAgent', { phone: SEO_CONFIG.AGENT_PHONE })}</a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>Search Santa Ana</button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.</span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default SantaAnaVietnameseMarketRealEstatePage;
